// 🧠 Servicio de Analítica Inteligente para Caja Chica
// Detecta anomalías, genera predicciones y alertas

export interface Gasto {
  id: string | number;
  fecha: string;
  monto: number;
  descripcion: string;
  categoria: string;
  trabajador: string;
  sincronizado: boolean;
  aprobado?: boolean;
  fechaModificacion?: string;
  monto_ocr?: number | string | null; // Monto detectado por OCR (puede venir como number, string o null)
  estado_alerta?: 'pendiente' | 'aprobado' | 'rechazado'; // Estado de alerta del gasto
  documento?: string | null; // URL del documento/comprobante
  fechaSincronizacion?: string; // Fecha de sincronización
}

export interface Anomalia {
  id: string;
  tipo: 'duplicado' | 'fuera_presupuesto' | 'monto_inusual' | 'categoria_inusual' | 'monto_inconsistente';
  severidad: 'rojo' | 'amarillo' | 'verde';
  descripcion: string;
  gasto: Gasto;
  monto: number;
  fecha: string;
  estado: 'pendiente' | 'aprobado' | 'rechazado';
  trabajador?: string; // Campo opcional para compatibilidad
}

export interface Prediccion {
  gastoProyectado: number;
  confianza: number; // 0-100
  tendencia: 'ascendente' | 'descendente' | 'estable';
  variacionPorcentual: number;
  factores: string[];
}

// Detectar gastos duplicados
export function detectarGastosDuplicados(gastos: Gasto[]): Anomalia[] {
  const anomalias: Anomalia[] = [];
  const gastosAgrupados = new Map<string, Gasto[]>();

  // Agrupar gastos por descripción, monto y fecha (mismo día)
  gastos.forEach(gasto => {
    const key = `${gasto.descripcion.toLowerCase()}_${gasto.monto}_${gasto.fecha}`;
    if (!gastosAgrupados.has(key)) {
      gastosAgrupados.set(key, []);
    }
    gastosAgrupados.get(key)!.push(gasto);
  });

  // Detectar duplicados (más de 1 gasto con mismo descripción, monto y fecha)
  gastosAgrupados.forEach((grupo, key) => {
    if (grupo.length > 1) {
      grupo.forEach((gasto, index) => {
        if (index > 0) { // El primero no es duplicado
          anomalias.push({
            id: `duplicado_${gasto.id}_${index}`,
            tipo: 'duplicado',
            severidad: 'amarillo',
            descripcion: `Gasto duplicado detectado: ${gasto.descripcion} ($${gasto.monto.toLocaleString()})`,
            gasto,
            monto: gasto.monto,
            fecha: gasto.fecha,
            estado: 'pendiente' // ✅ Siempre iniciar como pendiente - el estado se gestiona en React, no en el gasto
          });
        }
      });
    }
  });

  return anomalias;
}

// Detectar gastos fuera de presupuesto
export function detectarGastosFueraPresupuesto(
  gastos: Gasto[], 
  presupuestoMensual: number,
  porcentajeAlerta: number = 80
): Anomalia[] {
  const anomalias: Anomalia[] = [];
  const gastoTotal = gastos.reduce((sum, g) => sum + g.monto, 0);
  const porcentajeUtilizado = (gastoTotal / presupuestoMensual) * 100;

  // Si se ha utilizado más del porcentaje de alerta
  if (porcentajeUtilizado > porcentajeAlerta) {
    gastos.forEach(gasto => {
      // Gastos individuales que superan el 10% del presupuesto mensual
      const umbralGastoIndividual = presupuestoMensual * 0.1;
      if (gasto.monto > umbralGastoIndividual) {
        anomalias.push({
          id: `fuera_presupuesto_${gasto.id}`,
          tipo: 'fuera_presupuesto',
          severidad: porcentajeUtilizado > 90 ? 'rojo' : 'amarillo',
          descripcion: `Gasto individual fuera de presupuesto: ${gasto.descripcion} ($${gasto.monto.toLocaleString()})`,
          gasto,
          monto: gasto.monto,
          fecha: gasto.fecha,
          estado: 'pendiente' // ✅ Siempre iniciar como pendiente - el estado se gestiona en React, no en el gasto
        });
      }
    });
  }

  return anomalias;
}

// Detectar montos inusuales (usando desviación estándar)
export function detectarMontosInusuales(gastos: Gasto[]): Anomalia[] {
  const anomalias: Anomalia[] = [];
  
  if (gastos.length < 3) return anomalias; // Necesitamos al menos 3 gastos

  const montos = gastos.map(g => g.monto);
  const promedio = montos.reduce((sum, m) => sum + m, 0) / montos.length;
  const varianza = montos.reduce((sum, m) => sum + Math.pow(m - promedio, 2), 0) / montos.length;
  const desviacionEstandar = Math.sqrt(varianza);
  const umbral = promedio + (2 * desviacionEstandar); // 2 desviaciones estándar

  gastos.forEach(gasto => {
    if (gasto.monto > umbral) {
      anomalias.push({
        id: `monto_inusual_${gasto.id}`,
        tipo: 'monto_inusual',
        severidad: gasto.monto > promedio + (3 * desviacionEstandar) ? 'rojo' : 'amarillo',
        descripcion: `Monto inusual detectado: $${gasto.monto.toLocaleString()} (promedio: $${Math.round(promedio).toLocaleString()})`,
        gasto,
        monto: gasto.monto,
        fecha: gasto.fecha,
          estado: 'pendiente' // ✅ Siempre iniciar como pendiente - el estado se gestiona en React, no en el gasto
      });
    }
  });

  return anomalias;
}

// Detectar todas las anomalías
export function detectarTodasAnomalias(
  gastos: Gasto[],
  presupuestoMensual: number = 5000000
): Anomalia[] {
  const anomalias: Anomalia[] = [];

  // Combinar todas las detecciones
  anomalias.push(...detectarGastosDuplicados(gastos));
  anomalias.push(...detectarGastosFueraPresupuesto(gastos, presupuestoMensual));
  anomalias.push(...detectarMontosInusuales(gastos));

  // Nueva validación: Detectar inconsistencias entre monto y monto_ocr
  // ❌ REMOVER: Log inicial repetitivo
  let gastosConMontoOcr = 0;
  let anomaliasDetectadas = 0;

  gastos.forEach(gasto => {
    // ✅ MEJORADO: Validar monto_ocr de forma robusta (maneja string, number, null, undefined)
    // Nota: Los datos pueden venir como string del backend, así que validamos también cadena vacía
    let montoOcr: number | null = null;
    const montoOcrRaw = gasto.monto_ocr;
    
    if (montoOcrRaw !== undefined && montoOcrRaw !== null) {
      // Verificar si es cadena vacía (cuando viene del backend como string)
      if (typeof montoOcrRaw === 'string' && montoOcrRaw.trim() === '') {
        montoOcr = null;
      } else if (typeof montoOcrRaw === 'number') {
        montoOcr = montoOcrRaw;
      } else if (typeof montoOcrRaw === 'string') {
        const parsed = Number(montoOcrRaw);
        if (!isNaN(parsed)) {
          montoOcr = parsed;
        }
      }
    }
    
    // Contar gastos con monto_ocr (sin log individual para reducir ruido)
    if (gasto.monto_ocr !== undefined && gasto.monto_ocr !== null) {
      gastosConMontoOcr++;
    }
    
    // Si tenemos un monto_ocr válido, verificar diferencia
    if (montoOcr !== null && typeof montoOcr === 'number' && typeof gasto.monto === 'number') {
      const diferencia = Math.abs(montoOcr - gasto.monto);
      
      if (diferencia > 100) {
        anomaliasDetectadas++;
        anomalias.push({
          id: `monto_inconsistente_${gasto.id}`,
          tipo: 'monto_inconsistente',
          descripcion: `El monto ingresado ($${gasto.monto.toLocaleString('es-CL')}) no coincide con el total detectado en la boleta ($${montoOcr.toLocaleString('es-CL')}).`,
          severidad: 'rojo',
          estado: 'pendiente', // ✅ Siempre iniciar como pendiente - el estado se gestiona en React, no en el gasto
          monto: gasto.monto,
          fecha: gasto.fecha,
          trabajador: gasto.trabajador,
          gasto: gasto
        });
      }
    }
  });

  // Logs removidos para reducir ruido en consola

  // Eliminar duplicados (misma anomalía detectada por múltiples métodos)
  // ✅ IMPORTANTE: Las anomalías NUEVAS siempre deben empezar como 'pendiente'
  // Esto asegura que las alertas nuevas siempre requieran revisión manual
  const anomaliasUnicas = new Map<string, Anomalia>();
  anomalias.forEach(anomalia => {
    const key = `${anomalia.tipo}_${anomalia.gasto.id}`;
    if (!anomaliasUnicas.has(key)) {
      // Anomalía nueva: siempre empezar como 'pendiente'
      anomalia.estado = 'pendiente';
      anomaliasUnicas.set(key, anomalia);
    }
  });

  return Array.from(anomaliasUnicas.values());
}

// Generar predicción simple basada en tendencia lineal
export function generarPrediccion(gastos: Gasto[], presupuestoMaximo?: number): Prediccion {
  if (gastos.length < 7) {
    // Si hay menos de 7 días de datos, usar promedio simple con ajuste conservador
    const gastoTotal = gastos.reduce((sum, g) => sum + g.monto, 0);
    const diasConDatos = new Set(gastos.map(g => g.fecha)).size;
    const promedioDiario = diasConDatos > 0 ? gastoTotal / diasConDatos : 0;
    // Proyección mensual: promedio diario * 30 días con ajuste conservador del 5%
    let gastoProyectado = promedioDiario * 30 * 1.05;

    // LIMITAR al presupuesto máximo si está definido
    if (presupuestoMaximo && gastoProyectado > presupuestoMaximo) {
      gastoProyectado = presupuestoMaximo;
    }

    // Calcular variación porcentual respecto al promedio actual
    const promedioActual = promedioDiario * 30;
    const variacionPorcentual = promedioActual > 0 
      ? ((gastoProyectado - promedioActual) / promedioActual) * 100 
      : 5;

    const factores: string[] = ['Datos insuficientes para predicción precisa'];
    if (presupuestoMaximo && gastoProyectado >= presupuestoMaximo) {
      factores.push('Proyección limitada por presupuesto disponible');
    }

    return {
      gastoProyectado: Math.round(gastoProyectado),
      confianza: 30,
      tendencia: 'estable',
      variacionPorcentual: Math.round(variacionPorcentual * 100) / 100,
      factores
    };
  }

  // Agrupar gastos por día
  const gastosPorDia = new Map<string, number>();
  gastos.forEach(gasto => {
    const dia = gasto.fecha;
    gastosPorDia.set(dia, (gastosPorDia.get(dia) || 0) + gasto.monto);
  });

  // Ordenar por fecha
  const diasOrdenados = Array.from(gastosPorDia.entries())
    .sort((a, b) => a[0].localeCompare(b[0]));

  // Calcular tendencia lineal simple (regresión lineal básica)
  const n = diasOrdenados.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;

  diasOrdenados.forEach(([_, monto], index) => {
    const x = index + 1;
    const y = monto;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  // Calcular pendiente (m) y ordenada al origen (b)
  const pendiente = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const ordenada = (sumY - pendiente * sumX) / n;

  // Proyectar para los próximos 30 días
  let gastoProyectado = Math.round(pendiente * 30 + ordenada * 30);

  // LIMITAR al presupuesto máximo si está definido
  if (presupuestoMaximo && gastoProyectado > presupuestoMaximo) {
    gastoProyectado = presupuestoMaximo;
  }

  // Calcular promedio actual
  const promedioActual = sumY / n;

  // Determinar tendencia
  let tendencia: 'ascendente' | 'descendente' | 'estable' = 'estable';
  if (pendiente > promedioActual * 0.05) {
    tendencia = 'ascendente';
  } else if (pendiente < -promedioActual * 0.05) {
    tendencia = 'descendente';
  }

  // Calcular variación porcentual
  const variacionPorcentual = promedioActual * 30 > 0
    ? ((gastoProyectado - promedioActual * 30) / (promedioActual * 30)) * 100
    : 0;

  // Calcular confianza basada en cantidad de datos y variabilidad
  const varianza = diasOrdenados.reduce((sum, [_, monto]) => {
    return sum + Math.pow(monto - promedioActual, 2);
  }, 0) / n;
  const desviacionEstandar = Math.sqrt(varianza);
  const coeficienteVariacion = promedioActual > 0 ? desviacionEstandar / promedioActual : 0;
  const confianza = Math.max(30, Math.min(90, 100 - (coeficienteVariacion * 100)));

  // Factores que influyen en la predicción
  const factores: string[] = [];
  if (n < 14) factores.push('Pocos días de datos históricos');
  if (coeficienteVariacion > 0.5) factores.push('Alta variabilidad en gastos');
  if (pendiente > 0) factores.push('Tendencia ascendente detectada');
  if (pendiente < 0) factores.push('Tendencia descendente detectada');
  if (presupuestoMaximo && gastoProyectado >= presupuestoMaximo) {
    factores.push('Proyección limitada por presupuesto disponible');
  }

  return {
    gastoProyectado: Math.max(0, gastoProyectado), // No permitir valores negativos
    confianza: Math.round(confianza),
    tendencia,
    variacionPorcentual: Math.round(variacionPorcentual * 100) / 100,
    factores
  };
}

// Calcular desviación estándar de gastos diarios
export function calcularDesviacionEstandar(gastos: Gasto[]): number {
  if (gastos.length === 0) return 0;

  // Agrupar gastos por día
  const gastosPorDia = new Map<string, number>();
  gastos.forEach(gasto => {
    const dia = gasto.fecha;
    gastosPorDia.set(dia, (gastosPorDia.get(dia) || 0) + gasto.monto);
  });

  const montosDiarios = Array.from(gastosPorDia.values());
  if (montosDiarios.length === 0) return 0;

  const promedio = montosDiarios.reduce((sum, m) => sum + m, 0) / montosDiarios.length;
  const varianza = montosDiarios.reduce((sum, m) => sum + Math.pow(m - promedio, 2), 0) / montosDiarios.length;

  return Math.round(Math.sqrt(varianza));
}

